import { RestClient } from "@infrastructure/clients/http/clients/base/rest.client";

export interface HTTP2PushOptions {
  onMessage: (data: any) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
}

export abstract class HTTP2PushClient extends RestClient {
  abstract baseUrl: string;
  private pushSubscriptions = new Map<string, Set<(data: any) => void>>();
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

  async initialize(): Promise<void> {
    if (!("serviceWorker" in navigator)) {
      throw new Error("HTTP/2 Server Push requires Service Worker support");
    }

    try {
      this.serviceWorkerRegistration = await navigator.serviceWorker.register("/sw.js");
      console.log("[HTTP2 Push] Service Worker registered");

      // Слушаем сообщения от Service Worker
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data.type === "push") {
          this.handlePushEvent(event.data.resource, event.data.data);
        }
      });
    } catch (error) {
      console.error("[HTTP2 Push] Failed to register Service Worker:", error);
      throw error;
    }
  }

  async subscribeToPush(
    resourcePath: string,
    options: HTTP2PushOptions
  ): Promise<() => void> {
    if (!this.serviceWorkerRegistration) {
      await this.initialize();
    }

    const handler = (data: any) => {
      try {
        options.onMessage(data);
      } catch (error) {
        options.onError?.(error as Error);
      }
    };

    if (!this.pushSubscriptions.has(resourcePath)) {
      this.pushSubscriptions.set(resourcePath, new Set());
    }

    this.pushSubscriptions.get(resourcePath)!.add(handler);

    // Регистрируем подписку через специальный endpoint
    await this.request("POST", "/api/http2-push/subscribe", {
      jsonPayload: {
        resource: resourcePath,
        action: "subscribe",
      },
    });

    return () => {
      this.unsubscribeFromPush(resourcePath, handler);
    };
  }

  private async unsubscribeFromPush(resourcePath: string, handler: (data: any) => void): Promise<void> {
    this.pushSubscriptions.get(resourcePath)?.delete(handler);
    if (this.pushSubscriptions.get(resourcePath)?.size === 0) {
      this.pushSubscriptions.delete(resourcePath);
      await this.request("POST", "/api/http2-push/subscribe", {
        jsonPayload: {
          resource: resourcePath,
          action: "unsubscribe",
        },
      });
    }
  }

  // Метод для обработки push событий из Service Worker
  private handlePushEvent(resourcePath: string, data: any): void {
    const handlers = this.pushSubscriptions.get(resourcePath);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  }
}

